/**
 * The choices behind the ticket list's typable Category cell, ported from the Outlook pane's combobox
 * (`src/outlook/lookups.ts` buildOptions, `src/outlook/main.ts` matchingOptions / resolveOption on the
 * outlook-addin branch). No React and no app state, so it can be tested on its own.
 */

export interface CategoryRecord {
  Id: number;
  Value: string;
  ParentId?: number | null;
  Position?: number;
  IsArchived?: boolean;
  IsDeleted?: boolean;
}

export interface CategoryOption {
  id: number;
  /** The full path, "Hardware > Computer > Laptop": two "Laptop"s under different parents must read apart. */
  name: string;
  /** How many levels down a category sits (0 for a top one). While nothing is typed the list shows the tree by it. */
  depth?: number;
  /** A value the field still holds but no longer offers (withHeldOptions): it names the value and is never listed. */
  held?: boolean;
}

// The list's own separator (showParentChild's ' > '), so a value reads the same in the picker, while its save is out
// and once the row reloads - with '›' here, the separator visibly changed size as the save landed.
const SEPARATOR = ' > ';

/** A record's full path, walked up through the parents `byId` knows. */
const pathOf = (record: CategoryRecord, byId: Map<number, CategoryRecord>): string => {
  const parts = [record.Value];
  const seen = new Set<number>([record.Id]);
  let parent = record.ParentId != null ? byId.get(record.ParentId) : undefined;
  while (parent && !seen.has(parent.Id)) {
    parts.unshift(parent.Value);
    seen.add(parent.Id);
    parent = parent.ParentId != null ? byId.get(parent.ParentId) : undefined;
  }
  return parts.join(SEPARATOR);
};

/**
 * `options` plus the picked values they no longer offer - archived or deleted since they were picked - named from
 * `records`, which must include them. Without these a field showed a held value as blank, and a form read the blank
 * as cleared (an archived category emptied Edit Ticket's Category and held Save back); a several-pick box dropped
 * them on the next change. They are not offered (matchingOptions), so one taken off cannot be picked again.
 */
export function withHeldOptions(
  options: CategoryOption[], records: CategoryRecord[], pickedIds: (number | null | undefined)[]
): CategoryOption[] {
  const offered = new Set(options.map(option => option.id));
  const byId = new Map((records ?? []).map(record => [record.Id, record]));
  const held = [...new Set(pickedIds)]
    .filter((id): id is number => id != null && !offered.has(id) && byId.has(id))
    .map(id => ({ id, name: pathOf(byId.get(id), byId), held: true }));
  return held.length > 0 ? [...options, ...held] : options;
}

/** Depth-first in Position order, archived categories and everything under them left out. */
export function buildCategoryOptions(records: CategoryRecord[]): CategoryOption[] {
  const byId = new Map((records ?? []).map(record => [record.Id, record]));

  const isVisible = (record: CategoryRecord): boolean => {
    const seen = new Set<number>();
    let current: CategoryRecord | undefined = record;
    while (current && !seen.has(current.Id)) {
      if (current.IsArchived || current.IsDeleted) return false;
      seen.add(current.Id);
      current = current.ParentId != null ? byId.get(current.ParentId) : undefined;
    }
    return true;
  };

  const label = (record: CategoryRecord): string => pathOf(record, byId);

  // A parent outside the list is treated as a root, so nothing is stranded.
  const parentOf = (record: CategoryRecord): number | null =>
    record.ParentId != null && byId.has(record.ParentId) ? record.ParentId : null;

  const childrenOf = (parentId: number | null): CategoryRecord[] =>
    [...byId.values()]
      .filter(record => parentOf(record) === parentId)
      .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0) || a.Value.localeCompare(b.Value));

  const options: CategoryOption[] = [];
  const walk = (parentId: number | null, depth: number): void => {
    if (depth > 20) return;
    for (const record of childrenOf(parentId)) {
      if (isVisible(record)) options.push({ id: record.Id, name: label(record), depth });
      walk(record.Id, depth + 1);
    }
  };
  walk(null, 0);
  return options;
}

/** Case-insensitive substring match anywhere in the path. A held value (withHeldOptions) is never listed. */
export function matchingOptions(options: CategoryOption[], typed: string): CategoryOption[] {
  const needle = typed.trim().toLowerCase();
  const offered = options.some(option => option.held) ? options.filter(option => !option.held) : options;
  if (!needle) return offered;
  return offered.filter(option => option.name.toLowerCase().includes(needle));
}

/**
 * What was typed, as a category id: the full path, or the last level on its own when only one category
 * ends that way - "Printing" finds "Hardware > Printing".
 */
export function resolveOption(options: CategoryOption[], typed: string): number | null {
  const needle = typed.trim().toLowerCase();
  if (!needle) return null;

  const exact = options.find(option => option.name.toLowerCase() === needle);
  if (exact) return exact.id;

  const leaf = options.filter(option => {
    const parts = option.name.split(SEPARATOR);
    return (parts[parts.length - 1] ?? '').trim().toLowerCase() === needle;
  });
  return leaf.length === 1 ? leaf[0].id : null;
}

export function optionName(options: CategoryOption[], id: number | null | undefined): string {
  return options.find(option => option.id === id)?.name ?? '';
}

/**
 * The label cut into runs, each marked whether it is part of what was typed - every occurrence, any case -
 * so the list can draw the typed letters bold.
 */
export interface LabelRun { text: string; match: boolean; /** Part of a parent's name, drawn apart from the category's own. */ parent: boolean; start: number; }

/**
 * What a list row shows. While nothing is typed: the category's own name, indented by its depth, so the list reads
 * as the tree. Once something is typed: the whole path, with what was typed marked and the parents set apart from
 * the category itself - a match deep in the tree still says where it sits.
 */
export function rowLabel(option: CategoryOption, typed: string): { indent: number; runs: LabelRun[] } {
  const lastSeparator = option.name.lastIndexOf(SEPARATOR);
  const ownStart = lastSeparator < 0 ? 0 : lastSeparator + SEPARATOR.length;
  const depth = option.depth ?? 0;
  if (!typed.trim() && depth > 0) {
    return { indent: depth, runs: [{ text: option.name.slice(ownStart), match: false, parent: false, start: ownStart }] };
  }
  const runs: LabelRun[] = [];
  let start = 0;
  matchRuns(option.name, typed).forEach(run => {
    const end = start + run.text.length;
    if (start < ownStart && end > ownStart) {
      runs.push({ text: run.text.slice(0, ownStart - start), match: run.match, parent: true, start });
      runs.push({ text: run.text.slice(ownStart - start), match: run.match, parent: false, start: ownStart });
    } else {
      runs.push({ text: run.text, match: run.match, parent: start < ownStart, start });
    }
    start = end;
  });
  return { indent: 0, runs };
}

/**
 * Each row as the list draws it, for sizing the list: the indented name while nothing is typed, the full path once
 * something is. Every option counts, not only the matches, so the width holds while the agent types.
 */
export function listWidthRows(options: CategoryOption[], typed: string): { text: string; indent: number }[] {
  return options.map(option => {
    const { indent, runs } = rowLabel(option, typed);
    return { text: runs.map(run => run.text).join(''), indent };
  });
}

export function matchRuns(label: string, typed: string): { text: string; match: boolean }[] {
  const needle = typed.trim().toLowerCase();
  if (!needle) return [{ text: label, match: false }];
  const runs: { text: string; match: boolean }[] = [];
  const lower = label.toLowerCase();
  let from = 0;
  for (let at = lower.indexOf(needle); at >= 0; at = lower.indexOf(needle, at + needle.length)) {
    if (at > from) runs.push({ text: label.slice(from, at), match: false });
    runs.push({ text: label.slice(at, at + needle.length), match: true });
    from = at + needle.length;
  }
  if (from < label.length) runs.push({ text: label.slice(from), match: false });
  return runs;
}
